import { useEffect, useState, useRef } from 'react';
import Modal from '@/components/base/Modal';
import CustomSelect from '@/components/base/CustomSelect';
import { rewardCategories } from '@/mocks/rewards';
import type { Reward, RewardDraft } from '@/types/reward';
import { validateRewardInput } from '@/lib/rewards/validation';

interface RewardFormModalProps {
  open: boolean;
  reward: Reward | null;
  onClose: () => void;
  onSave: (draft: RewardDraft) => void;
}

const categoryOptions = rewardCategories.filter((category) => category !== 'All categories');

const DEFAULT_PLACEHOLDER =
  'https://placehold.co/600x400/f1f5f9/94a3b8?text=Reward';

export default function RewardFormModal({ open, reward, onClose, onSave }: RewardFormModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Vouchers');
  const [pointsCost, setPointsCost] = useState('');
  const [stock, setStock] = useState('');
  const [status, setStatus] = useState<'active' | 'disabled'>('active');
  const [image, setImage] = useState('');
  const [imageTab, setImageTab] = useState<'upload' | 'url'>('upload');
  const [urlInput, setUrlInput] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    if (reward) {
      setName(reward.name);
      setDescription(reward.description);
      setCategory(reward.category);
      setPointsCost(String(reward.pointsCost));
      setStock(String(reward.stock));
      setStatus(reward.status);
      setImage(reward.image || '');
      setUrlInput(reward.image || '');
    } else {
      setName('');
      setDescription('');
      setCategory('Vouchers');
      setPointsCost('1200');
      setStock('100');
      setStatus('active');
      setImage('');
      setUrlInput('');
    }
    setImageTab('upload');
    setError('');
  }, [open, reward]);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (PNG, JPG, WebP).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setImage(reader.result);
        setError('');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleApplyUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setError('Please enter a valid image URL.');
      return;
    }
    setImage(trimmed);
    setError('');
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = name.trim();
    const cost = Number(pointsCost);
    const stockValue = stock.trim() === '' ? NaN : Number(stock);
    const validationError = validateRewardInput({ name: trimmedName, pointsCost: cost, stock: stockValue });
    if (validationError) {
      setError(validationError);
      return;
    }

    onSave({
      id: reward?.id ?? `rwd-${Date.now()}`,
      name: trimmedName,
      description: description.trim(),
      category,
      pointsCost: cost,
      stock: stockValue,
      status,
      image: image.trim() || DEFAULT_PLACEHOLDER,
    });
  };

  const inputClasses =
    'w-full h-12 px-5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all shadow-xs';

  return (
    <Modal
      open={open}
      title={reward ? 'Edit reward' : 'Create reward'}
      subtitle={reward ? 'Update the details of this reward.' : 'Add a new reward to your catalog.'}
      onClose={onClose}
      maxWidth="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
          className="hidden"
        />

        {/* 1. Name */}
        <div className="flex flex-col gap-2">
          <label htmlFor="reward-name" className="text-sm font-semibold text-foreground-800 tracking-wide">
            Name
          </label>
          <input
            id="reward-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Specialty Coffee Voucher"
            className={inputClasses}
          />
        </div>

        {/* 2. Reward Image - Clean, Spacious & Intuitive */}
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-foreground-800 tracking-wide">Reward image</label>
            <div className="inline-flex rounded-lg bg-background-100 p-1 border border-background-200 text-xs">
              <button
                type="button"
                onClick={() => setImageTab('upload')}
                className={`px-3 py-1.5 rounded-md font-medium transition-all cursor-pointer ${
                  imageTab === 'upload' ? 'bg-background-50 text-foreground-950 shadow-xs font-semibold' : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                Upload photo
              </button>
              <button
                type="button"
                onClick={() => setImageTab('url')}
                className={`px-3 py-1.5 rounded-md font-medium transition-all cursor-pointer ${
                  imageTab === 'url' ? 'bg-background-50 text-foreground-950 shadow-xs font-semibold' : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                Paste URL
              </button>
            </div>
          </div>

          {/* Image preview and actions */}
          <div className="flex items-center gap-4 p-4 sm:p-5 rounded-2xl border border-background-200 bg-background-100/40">
            {image ? (
              <div className="relative group w-16 h-16 rounded-xl overflow-hidden border border-background-200/90 bg-background-200 shrink-0 shadow-xs">
                <img
                  src={image}
                  alt="Reward preview"
                  className="w-full h-full object-cover"
                  onError={() => setImage('')}
                />
                <button
                  type="button"
                  onClick={() => {
                    setImage('');
                    setUrlInput('');
                  }}
                  className="absolute inset-0 bg-foreground-950/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs transition-opacity cursor-pointer"
                  title="Remove photo"
                >
                  <i className="ri-delete-bin-line text-base"></i>
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-16 h-16 rounded-xl border border-dashed border-background-300 hover:border-primary-400 bg-background-100/70 hover:bg-background-200/60 flex flex-col items-center justify-center shrink-0 cursor-pointer transition-colors group"
                title="Click to choose photo"
              >
                <i className="ri-image-add-line text-2xl text-foreground-400 group-hover:text-primary-600 transition-colors"></i>
                <span className="text-[10px] text-foreground-400 font-medium mt-0.5">Photo</span>
              </div>
            )}

            <div className="flex-1 min-w-0">
              {imageTab === 'upload' ? (
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`flex items-center gap-3.5 px-4 py-3 rounded-xl border border-dashed text-xs cursor-pointer transition-all ${
                    isDragging ? 'border-primary-500 bg-primary-50/30' : 'border-background-300 hover:border-primary-400 bg-background-50 hover:bg-background-100/70 shadow-xs'
                  }`}
                >
                  <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                    <i className="ri-upload-cloud-2-line text-lg"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-foreground-800">
                      <span className="text-primary-600 hover:underline">Choose photo</span> or drag here
                    </p>
                    <p className="text-[11px] text-foreground-400 mt-0.5">PNG, JPG, WebP up to 5MB</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2.5">
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="Paste link: https://..."
                    className="flex-1 h-11 px-4 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 shadow-xs"
                  />
                  <button
                    type="button"
                    onClick={handleApplyUrl}
                    className="h-11 px-4 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold transition-all cursor-pointer shadow-xs"
                  >
                    Set
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 3. Description */}
        <div className="flex flex-col gap-2">
          <label htmlFor="reward-description" className="text-sm font-semibold text-foreground-800 tracking-wide">
            Description
          </label>
          <textarea
            id="reward-description"
            rows={3}
            maxLength={500}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this reward include?"
            className="w-full min-h-[105px] p-5 bg-background-50 border border-background-200 rounded-xl text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-all resize-none shadow-xs"
          />
          <div className="flex justify-end pr-1">
            <span className="text-xs text-foreground-400 font-medium">{description.length}/500</span>
          </div>
        </div>

        {/* 4. Category & Points Cost */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="flex flex-col gap-2">
            <label htmlFor="reward-category" className="text-sm font-semibold text-foreground-800 tracking-wide">
              Category
            </label>
            <CustomSelect id="reward-category" value={category} onChange={setCategory} options={categoryOptions} size="lg" searchable={false} className="w-full" />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="reward-cost" className="text-sm font-semibold text-foreground-800 tracking-wide">
              Points cost
            </label>
            <input
              id="reward-cost"
              type="number"
              min={1}
              required
              value={pointsCost}
              onChange={(e) => setPointsCost(e.target.value)}
              placeholder="1200"
              className={inputClasses}
            />
          </div>
        </div>

        {/* 5. Stock & Status */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-end">
          <div className="flex flex-col gap-2">
            <label htmlFor="reward-stock" className="text-sm font-semibold text-foreground-800 tracking-wide">
              Stock
            </label>
            <input
              id="reward-stock"
              type="number"
              min={0}
              required
              value={stock}
              onChange={(e) => setStock(e.target.value)}
              placeholder="100"
              className={inputClasses}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-foreground-800 tracking-wide">Status</span>
            <div className="inline-flex items-center gap-1.5 p-1.5 rounded-xl bg-background-100 border border-background-200 h-12 w-full">
              <button
                type="button"
                onClick={() => setStatus('active')}
                className={`flex-1 h-full inline-flex items-center justify-center gap-2 px-3 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  status === 'active'
                    ? 'bg-emerald-500 text-white shadow-xs'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${status === 'active' ? 'bg-white animate-pulse' : 'bg-foreground-400'}`}></span>
                Active
              </button>
              <button
                type="button"
                onClick={() => setStatus('disabled')}
                className={`flex-1 h-full inline-flex items-center justify-center px-3 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  status === 'disabled'
                    ? 'bg-slate-700 text-white shadow-xs'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                Disabled
              </button>
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2.5 rounded-xl bg-rose-500/10 border border-rose-500/20 px-4 py-3 text-xs text-rose-800 animate-fade-in">
            <i className="ri-error-warning-fill text-rose-600 text-base shrink-0"></i>
            <span className="font-medium leading-relaxed">{error}</span>
          </div>
        )}

        {/* Footer buttons */}
        <div className="flex items-center justify-end gap-3 pt-6 mt-2 border-t border-background-200/80">
          <button
            type="button"
            onClick={onClose}
            className="h-11 px-5 rounded-xl bg-background-100 hover:bg-background-200 border border-background-200 text-sm font-medium text-foreground-700 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="h-11 px-6 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold shadow-xs hover:shadow-sm transition-all cursor-pointer"
          >
            {reward ? 'Save changes' : 'Create reward'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
